import { useCallback, useEffect, useRef, useState } from 'react';
const DEFAULT_HOVER_OPEN_DELAY_MS = 200;
const DEFAULT_HOVER_CLOSE_DELAY_MS = 300;
/**
 * Manages "pin on click, hover to preview" toggle-tip behavior.
 *
 * **Hover** opens the popover after a short delay and auto-closes it when
 * the pointer leaves both the trigger and the content.
 *
 * **Click** pins the popover open — it stays visible until the user clicks
 * the trigger again or dismisses it (e.g. clicking outside, pressing Escape).
 *
 * Supports both **controlled** and **uncontrolled** modes:
 * - Uncontrolled (default): the hook owns the open state internally.
 * - Controlled: pass `open` and `onOpenChange` to manage state externally.
 *   In controlled mode, hover timers also call `onOpenChange` — the consumer
 *   is responsible for updating `open` in response.
 *
 * @example
 * ```tsx
 * // Uncontrolled
 * const toggleTip = useToggleTip();
 *
 * // Controlled
 * const [open, setOpen] = useState(false);
 * const toggleTip = useToggleTip({ open, onOpenChange: setOpen });
 * ```
 */
export function useToggleTip({ open: controlledOpen, onOpenChange: controlledOnOpenChange, hoverOpenDelayMs = DEFAULT_HOVER_OPEN_DELAY_MS, hoverCloseDelayMs = DEFAULT_HOVER_CLOSE_DELAY_MS, } = {}) {
    const [internalOpen, setInternalOpen] = useState(false);
    const isControlled = controlledOpen !== undefined && controlledOnOpenChange !== undefined;
    const isOpen = isControlled ? controlledOpen : internalOpen;
    // In controlled mode this delegates to the consumer's `onOpenChange`,
    // so hover timers (open & close) also flow through the controlled callback.
    const setOpen = isControlled ? controlledOnOpenChange : setInternalOpen;
    // Tracks whether the popover was opened by hover or click.
    // Hover-opened: auto-closes on pointer leave.
    // Click-opened (pinned): stays open until toggled by click or outside dismissal.
    const openSourceRef = useRef(null);
    const openTimerRef = useRef(undefined);
    const closeTimerRef = useRef(undefined);
    const clearTimers = useCallback(() => {
        clearTimeout(openTimerRef.current);
        clearTimeout(closeTimerRef.current);
    }, []);
    // Clean up timers on unmount.
    useEffect(() => clearTimers, [clearTimers]);
    const startCloseTimer = useCallback(() => {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = setTimeout(() => {
            setOpen(false);
            openSourceRef.current = null;
        }, hoverCloseDelayMs);
    }, [setOpen, hoverCloseDelayMs]);
    const handlePointerEnter = useCallback(() => {
        clearTimers();
        if (isOpen)
            return;
        openTimerRef.current = setTimeout(() => {
            openSourceRef.current = 'hover';
            setOpen(true);
        }, hoverOpenDelayMs);
    }, [clearTimers, isOpen, setOpen, hoverOpenDelayMs]);
    const handlePointerLeave = useCallback(() => {
        clearTimeout(openTimerRef.current);
        if (openSourceRef.current !== 'hover')
            return;
        startCloseTimer();
    }, [startCloseTimer]);
    const handleContentMouseEnter = useCallback(() => {
        clearTimeout(closeTimerRef.current);
    }, []);
    const handleContentMouseLeave = useCallback(() => {
        if (openSourceRef.current !== 'hover')
            return;
        startCloseTimer();
    }, [startCloseTimer]);
    // Popover's onOpenChange handles click-to-toggle and outside dismissal.
    const handleOpenChange = useCallback((nextOpen) => {
        clearTimers();
        openSourceRef.current = nextOpen ? 'click' : null;
        setOpen(nextOpen);
    }, [clearTimers, setOpen]);
    return {
        isOpen,
        handlePointerEnter,
        handlePointerLeave,
        handleContentMouseEnter,
        handleContentMouseLeave,
        handleOpenChange,
    };
}
//# sourceMappingURL=useToggleTip.js.map